import React from "react";
import { Page, Text, View, Document, StyleSheet } from "@react-pdf/renderer";

/*
 * Subscription contract "ERP NORYX" (model supplied by IWARE SUARL). The
 * articles are the model's text; the bracketed fields of the model are filled
 * with the contract data, or left as a blank line to complete by hand when the
 * information is not known.
 */
const styles = StyleSheet.create({
  page: {
    fontFamily: "Helvetica",
    fontSize: 10,
    paddingTop: 50,
    paddingBottom: 55,
    paddingHorizontal: 50,
    lineHeight: 1.45,
    color: "#111",
  },
  header: {
    position: "absolute",
    top: 20,
    left: 50,
    right: 50,
    flexDirection: "row",
    justifyContent: "space-between",
    fontSize: 8,
    color: "#555",
    borderBottomWidth: 0.5,
    borderBottomColor: "#999",
    paddingBottom: 4,
  },
  title: { fontSize: 17, textAlign: "center", fontFamily: "Helvetica-Bold", marginTop: 6 },
  subtitle: { fontSize: 11, textAlign: "center", fontFamily: "Helvetica-Bold", marginTop: 2 },
  tagline: { textAlign: "center", color: "#555", marginTop: 3, marginBottom: 12 },
  metaRow: { flexDirection: "row", justifyContent: "center", marginBottom: 12 },
  metaBox: { borderWidth: 1, borderColor: "#999", padding: 6, width: "42%", marginHorizontal: 6 },
  metaLabel: { fontSize: 8, color: "#555" },
  bold: { fontFamily: "Helvetica-Bold" },
  between: { fontFamily: "Helvetica-Bold", marginTop: 6, marginBottom: 6 },
  party: { marginBottom: 6, textAlign: "justify" },
  article: { fontFamily: "Helvetica-Bold", marginTop: 10, marginBottom: 3, fontSize: 10.5 },
  text: { textAlign: "justify" },
  finTitle: { fontFamily: "Helvetica-Bold", marginTop: 14, marginBottom: 6 },
  signatures: { flexDirection: "row", justifyContent: "space-between", marginTop: 10 },
  signature: { width: "47%", borderWidth: 1, borderColor: "#999", padding: 8, minHeight: 130 },
  sigLine: { marginTop: 6 },
  note: { marginTop: 12, fontSize: 9, color: "#444" },
  footer: {
    position: "absolute",
    bottom: 22,
    left: 50,
    right: 50,
    textAlign: "center",
    fontSize: 8,
    color: "#555",
  },
});

// The Prestataire of the contract is always IWARE (data of its company record)
export const IWARE_PROVIDER = {
  title: "IWARE SUARL",
  matricule: "1533955/Q",
  adresse: "Av. Perle du Sahel, Immeuble Graeit, bureau 4, Khzema, Sousse",
  email: "contact@iware.info",
  numPhone: "31572542",
};
// default VAT rate of the contract (editable in the form)
export const IWARE_VAT_RATE = 19;

const BLANK = "__________________";

const filled = (value) => (value !== undefined && value !== null && String(value).trim() !== "" ? String(value) : BLANK);

const formatDate = (iso) => {
  if (!iso) return BLANK;
  const [y, m, d] = String(iso).split("-");
  return `${d}/${m}/${y}`;
};

const periodAdjective = (p) => (p === "ANNUEL" ? "Annuelle" : "Mensuelle");
const money = (n) => Number(n || 0).toFixed(3);

const Header = () => (
  <View style={styles.header} fixed>
    <Text>IWARE SUARL - Contrat d'abonnement ERP NORYX</Text>
    <Text render={({ pageNumber, totalPages }) => `Page ${pageNumber} / ${totalPages}`} />
  </View>
);

const Article = ({ title, children }) => (
  <View>
    <Text style={styles.article}>{title}</Text>
    <Text style={styles.text}>{children}</Text>
  </View>
);

// contract: { ref, amount (HT), periodicity, startDate, endDate }
// the Prestataire is always IWARE; extras (all optional): tvaRate, paymentMode, paymentDelay, noticeDays, city, signatureDate,
//   issuerRep, clientRep, clientLegalForm
const ClientContractPdf = ({ contract, client = {}, extras = {} }) => {
  const issuer = IWARE_PROVIDER;
  const periodicity = periodAdjective(contract.periodicity);
  const ht = Number(contract.amount) || 0;
  const rate = extras.tvaRate !== undefined && extras.tvaRate !== "" ? Number(extras.tvaRate) : IWARE_VAT_RATE;
  const tva = (ht * rate) / 100;
  const ttc = ht + tva;
  const issuerName = issuer.title;

  // identity and contact details of the client come from its company record
  const clientAddress = [client.adresse, [client.postalCode, client.city].filter(Boolean).join(" "), client.country]
    .filter((part) => part && String(part).trim() !== "")
    .join(", ");
  const clientPhone = client.numPhone || client.managerPhone;
  const clientEmail = client.email || client.managerEmail;

  return (
    <Document title={`Contrat ${contract.ref || ""}`}>
      <Page size="A4" style={styles.page}>
        <Header />
        <Text style={styles.title}>CONTRAT D'ABONNEMENT</Text>
        <Text style={styles.subtitle}>À L'ERP NORYX</Text>
        <Text style={styles.tagline}>IWARE SUARL - Solution de gestion d'entreprise</Text>

        <View style={styles.metaRow}>
          <View style={styles.metaBox}>
            <Text style={styles.metaLabel}>Numéro du contrat</Text>
            <Text style={styles.bold}>{filled(contract.ref)}</Text>
          </View>
          <View style={styles.metaBox}>
            <Text style={styles.metaLabel}>Date de signature</Text>
            <Text style={styles.bold}>{extras.signatureDate ? formatDate(extras.signatureDate) : BLANK}</Text>
          </View>
        </View>

        <Text style={styles.between}>ENTRE LES SOUSSIGNÉS</Text>
        <Text style={styles.party}>
          <Text style={styles.bold}>Le Prestataire : </Text>
          {issuerName.toUpperCase()}, société de droit tunisien, matricule fiscal : {filled(issuer.matricule)},
          siège social : {filled(issuer.adresse)}, représentée par {filled(extras.issuerRep)}, téléphone :{" "}
          {filled(issuer.numPhone)}, e-mail : {filled(issuer.email)}, ci-après dénommée « le Prestataire ».
        </Text>
        <Text style={styles.party}>
          <Text style={styles.bold}>Le Client : </Text>
          {filled((client.title || "").toUpperCase())}, forme juridique :{" "}
          {filled(extras.clientLegalForm || client.legalForm)}, matricule fiscal : {filled(client.matricule)}
          {client.commerceRegister ? `, registre du commerce : ${client.commerceRegister}` : ""}, adresse :{" "}
          {filled(clientAddress)}, représentée par {filled(extras.clientRep || client.managerName)}, téléphone :{" "}
          {filled(clientPhone)}, e-mail : {filled(clientEmail)}, ci-après dénommé « le Client ».
        </Text>

        <Article title="ARTICLE 1 - OBJET DU CONTRAT">
          Le présent contrat définit les conditions dans lesquelles le Prestataire met à la disposition du Client un
          accès à la solution de gestion ERP NORYX, selon la formule souscrite. Les fonctionnalités, modules et
          services inclus sont ceux convenus entre les Parties et, le cas échéant, précisés dans une annexe.
        </Article>

        <Article title="ARTICLE 2 - DURÉE ET PÉRIODE D'ABONNEMENT">
          Le contrat prend effet le {formatDate(contract.startDate)}. Périodicité retenue : {periodicity}. La première
          période court du {formatDate(contract.startDate)} au {formatDate(contract.endDate)}. À l'issue de cette
          période, le contrat est renouvelé automatiquement pour une période de même durée, sauf résiliation ou
          non-renouvellement effectué conformément à l'article 3.
        </Article>

        <Article title="ARTICLE 3 - RENOUVELLEMENT AUTOMATIQUE ET RÉSILIATION">
          Le contrat est renouvelé automatiquement à chaque échéance pour une durée correspondant à la périodicité
          choisie : un mois pour l'abonnement mensuel ou un an pour l'abonnement annuel. Chaque Partie peut demander la
          résiliation ou le non-renouvellement par notification écrite adressée à l'autre Partie, dans le respect d'un
          préavis de {filled(extras.noticeDays)} jours avant l'échéance. La notification doit identifier le contrat et
          la date souhaitée de fin d'abonnement. Les conséquences d'une résiliation anticipée, notamment les sommes
          restant dues et les éventuels remboursements, sont déterminées selon les conditions convenues et les
          dispositions légales applicables. Toute modification de prix applicable au renouvellement doit être
          communiquée au Client dans les conditions prévues au contrat et par la réglementation applicable.
        </Article>

        <Article title="ARTICLE 4 - CONDITIONS FINANCIÈRES">
          Les conditions financières sont les suivantes : périodicité {periodicity} ; montant HT : {money(ht)} TND ;
          taux de TVA applicable : {rate} % ; montant TVA : {money(tva)} TND ; montant TTC : {money(ttc)} TND. La TVA
          est calculée à partir du montant HT et du taux applicable à la prestation, conformément à la réglementation
          fiscale en vigueur. Le montant TTC correspond au montant HT augmenté de la TVA et, le cas échéant, des autres
          taxes ou droits légalement exigibles. Mode de paiement : {filled(extras.paymentMode)}. Délai de paiement :{" "}
          {filled(extras.paymentDelay)}. Les frais couvrent uniquement les services mentionnés au contrat et ses
          annexes.
        </Article>

        <Article title="ARTICLE 5 - OBLIGATIONS DU PRESTATAIRE">
          Le Prestataire s'engage à mettre à disposition l'accès à NORYX conformément à la formule souscrite, fournir
          les informations nécessaires à la prise en main selon les modalités convenues, traiter les demandes
          d'assistance selon les conditions de support convenues, mettre en œuvre des mesures raisonnables de sécurité
          et informer le Client, dans la mesure du possible, des interruptions planifiées susceptibles d'affecter le
          service.
        </Article>

        <Article title="ARTICLE 6 - OBLIGATIONS DU CLIENT">
          Le Client s'engage à utiliser NORYX conformément à sa destination, fournir des informations exactes,
          préserver la confidentialité de ses identifiants, gérer les droits de ses utilisateurs, régler les factures
          aux échéances convenues et ne pas tenter de compromettre la sécurité ou le fonctionnement de la solution. Le
          Client demeure responsable de ses données, de leur exactitude et de la légitimité de leur traitement, sous
          réserve des obligations légales respectives des Parties.
        </Article>

        <Article title="ARTICLE 7 - DONNÉES ET CONFIDENTIALITÉ">
          Les Parties préservent la confidentialité des informations non publiques échangées dans le cadre du contrat.
          Lorsque NORYX implique le traitement de données à caractère personnel pour le compte du Client, les Parties
          définissent leurs rôles et obligations conformément à la législation applicable en Tunisie. Les conditions de
          conservation, d'accès, de restitution et de suppression des données à la fin du contrat doivent être
          précisées selon les caractéristiques techniques de NORYX et les obligations légales applicables.
        </Article>

        <Article title="ARTICLE 8 - DISPONIBILITÉ ET RESPONSABILITÉ">
          Le Prestataire met en œuvre des moyens raisonnables pour assurer le bon fonctionnement de NORYX. Sauf
          engagement spécifique convenu par écrit, aucune disponibilité permanente ne peut être garantie. Chaque Partie
          demeure responsable des dommages dont elle doit répondre conformément au droit applicable. Toute limitation
          de responsabilité doit être définie expressément et conformément aux règles impératives applicables.
        </Article>

        <Article title="ARTICLE 9 - SUSPENSION ET FIN DU CONTRAT">
          En cas de manquement contractuel, la Partie concernée peut adresser une mise en demeure afin qu'il y soit
          remédié dans le délai indiqué, sous réserve des règles légales applicables. En cas d'impayé, toute suspension
          de l'accès à NORYX doit respecter les conditions contractuelles et les obligations légales applicables. À la
          fin du contrat, les modalités de désactivation des accès, de restitution des données et de clôture des sommes
          dues sont mises en œuvre conformément aux dispositions convenues.
        </Article>

        <Article title="ARTICLE 10 - DROIT APPLICABLE ET LITIGES">
          Le présent contrat est soumis au droit tunisien, sous réserve de validation de cette clause au regard de la
          situation des Parties et des règles impératives applicables. Les Parties s'efforcent de résoudre à l'amiable
          tout différend relatif à l'interprétation ou à l'exécution du contrat. À défaut de règlement amiable, le
          litige est soumis aux juridictions compétentes conformément au droit applicable.
        </Article>

        <Article title="ARTICLE 11 - DISPOSITIONS FINALES">
          Toute modification du présent contrat doit être formalisée par écrit et acceptée par les Parties. Les
          annexes éventuellement signées font partie intégrante du contrat. Le présent contrat est établi en deux
          exemplaires destinés aux Parties, chacune reconnaissant avoir pris connaissance de ses dispositions.
        </Article>

        <View wrap={false}>
          <Text style={styles.finTitle}>FAIT ET SIGNÉ</Text>
          <Text>
            Fait à : {filled(extras.city)}        Le : {extras.signatureDate ? formatDate(extras.signatureDate) : BLANK}
          </Text>
          <View style={styles.signatures}>
            <View style={styles.signature}>
              <Text style={styles.bold}>POUR {issuerName.toUpperCase()} - LE PRESTATAIRE</Text>
              <Text style={styles.sigLine}>Nom et prénom : {filled(extras.issuerRep)}</Text>
              <Text style={styles.sigLine}>Qualité : {BLANK}</Text>
              <Text style={styles.sigLine}>Date : {BLANK}</Text>
              <Text style={styles.sigLine}>Signature et cachet :</Text>
            </View>
            <View style={styles.signature}>
              <Text style={styles.bold}>POUR LA SOCIÉTÉ CLIENTE - LE CLIENT</Text>
              <Text style={styles.sigLine}>Nom et prénom : {filled(extras.clientRep || client.managerName)}</Text>
              <Text style={styles.sigLine}>Qualité : {BLANK}</Text>
              <Text style={styles.sigLine}>Date : {BLANK}</Text>
              <Text style={styles.sigLine}>Signature et cachet :</Text>
            </View>
          </View>
          <Text style={styles.note}>
            Annexes éventuelles : détail des modules souscrits, conditions de support, conditions particulières et grille
            tarifaire.
          </Text>
        </View>
      </Page>
    </Document>
  );
};

export default ClientContractPdf;
